import { useTranslation } from "react-i18next";
import { Modal } from "../../components/Modal"
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../state/store";
import { useEffect, useState } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { Form } from "../../components/form/Form";
import { ReactiveFormInput } from "../../components/form/ReactiveFormInput";
import { ReactiveFormSelect } from "../../components/form/ReactiveFormSelect";
import { ReactiveFormInputArea } from "../../components/form/ReactiveFormInputArea";
import { FrequencyFormModel, newFrequencyInitialValues } from "../../models/FollowUp";
import { toast, ToastContainer } from "react-toastify";
import { LoadingMask } from "../../components/LoadingMask";
import { Toast } from "../../components/Toast";
import { createFollowUpMessage, updateFollowUpMessage } from "../../state/automations/action";
import { getLocalTimeZone, localDateFormat, localTimeFormat } from "../../utils/functions";
import { ReactiveFormRadioButtonSelect } from "../../components/form/ReactiveFormRadioButtonSelect";
import { ReactiveFormLocalDateInput } from "../../components/form/ReactiveFormLocalDateInput";
import moment from "moment";

interface Props {
    info: {
        show: boolean;
        update: boolean;
        followUp?: FrequencyFormModel;
        index?: number;
    };
    onClose: () => void;
}

export const FrequencyAutomation: React.FC<Props> = ({
    info,
    onClose
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch<AppDispatch>();
    const isLoadingFollowUpMessages = useSelector((state: RootState) => state.automations.isLoadingFollowUpMessages);
    const errorFollowUpMessages = useSelector((state: any) => state.automations.errorFollowUpMessages);
    const [showFollowUpError, setShowFollowUpError] = useState(false);
    const [isASpecificDate, setIsASpecificDate] = useState(false);

    useEffect(() => {
        if (showFollowUpError) {
            Toast(
                t(errorFollowUpMessages?.reason ?? "SOMETHING_WENT_WRONG"),
                t(errorFollowUpMessages?.cause_info)
            );
        }
    }, [showFollowUpError, errorFollowUpMessages, t]);

    const methods = useForm<FrequencyFormModel>({
        defaultValues: newFrequencyInitialValues
    })

    const { handleSubmit, control, reset, setValue, getValues } = methods;

    useEffect(() => {
        info.followUp ?  reset(info.followUp) : reset(newFrequencyInitialValues)
        if(info.followUp) {
            reset(info.followUp) 

            if(info.followUp.specific_date) {
                const date = localDateFormat(`${info.followUp.specific_date}`);
                const time = localTimeFormat(`${info.followUp.specific_date}`);
                setValue("specific_date", moment(date).toDate())
                setValue("specific_time", time)
                setValue("is_specific_date", '1')
                setIsASpecificDate(true)
            }
        } else {
            reset(newFrequencyInitialValues)
        }
    }, [info.followUp, reset])

    const changeMessageValue = (parameter: string) => {
        const messageValue = getValues("message");
        setValue("message", messageValue + parameter);
    }

    const onSubmit: SubmitHandler<FrequencyFormModel> = async (data: any) => {
        if(info.update) {
            let frequency: FrequencyFormModel;
            if(data.is_specific_date === "1") {
                const specificDate = moment(data.specific_date);
                const timeSplit = data.specific_time.split(":");
                specificDate.hours(timeSplit[0]).minutes(timeSplit[timeSplit.length - 1]).seconds(0);

                frequency = {
                    description: data.description,
                    specific_date: specificDate.toDate(),
                    is_specific_date_recurrent: data.is_specific_date_recurrent,
                    message: data.message,
                    is_specific_date: data.is_specific_date
                }
            } else {
                const timeZone = getLocalTimeZone();
                frequency = {
                    description: data.description,
                    frequency: data.frequency,
                    frequency_unit: data.frequency_unit,
                    frequency_time: data.frequency_time,
                    time_zone: timeZone,
                    message: data.message,
                    is_specific_date: data.is_specific_date
                }
            }
            
            dispatch(updateFollowUpMessage({ followUpId: info.followUp?.id!, followUp: frequency })).then((e) => {
                if (e.type === "followUp/updateFollowUpMessage/rejected") {
                    setShowFollowUpError(true);
                } else {
                    toast(t("SUCCESSFULLY_UPDATED_FOLLOW_UP"));
                    closeModal();
                }
            });
        } else {
            //Todo: add
            let frequency: FrequencyFormModel;
            if(data.is_specific_date === "1") {
                const specificDate = moment(data.specific_date);
                const timeSplit = data.specific_time.split(":");
                specificDate.hours(timeSplit[0]).minutes(timeSplit[timeSplit.length - 1]).seconds(0);

                frequency = {
                    description: data.description,
                    specific_date: specificDate.toDate(),
                    message: data.message,
                    is_specific_date_recurrent: data.is_specific_date_recurrent,
                    is_specific_date: data.is_specific_date
                }
            } else {
                const timeZone = getLocalTimeZone();
                frequency = {
                    description: data.description,
                    frequency: data.frequency,
                    frequency_unit: data.frequency_unit,
                    frequency_time: data.frequency_time,
                    time_zone: timeZone,
                    message: data.message,
                    is_specific_date: data.is_specific_date
                }
            }

            dispatch(createFollowUpMessage(frequency)).then((e) => {
                if (e.type === "followUp/createFollowUpMessage/rejected") {
                    setShowFollowUpError(true);
                } else {
                    toast(t("SUCCESSFULLY_SAVED_FOLLOW_UP"));
                    closeModal();
                }
            });
        }
    }

    const closeModal = () => {
        reset({});
        setIsASpecificDate(false)
        onClose();
    };

    const handleOnChangeIsSpecificDate = (e: any) => {
        if(e.target.value === '0') {
            setIsASpecificDate(false);
        } else {
            setIsASpecificDate(true);
        }
    }

    return (<>
        <Modal
            label={t('FREQUENCY_AUTOMATION')}
            isOpen={info.show}
            onClose={closeModal}
            onSave={handleSubmit(onSubmit)}
        >
            <Form handleOnSubmit={handleSubmit(onSubmit)}>
                <div className="flex flex-col w-full">

                    <ReactiveFormRadioButtonSelect
                        className={"md:w-1/1"}
                        name="is_specific_date"
                        label={""}
                        control={control}
                        isRequired
                        handleOnChange={handleOnChangeIsSpecificDate}
                        options={[
                            { label: t("FREQUENCY"), value: "0" },
                            { label: t("SPECIFIC_DATE"), value: "1" }
                        ]}
                    />

                    {
                        !isASpecificDate &&
                        <div>
                            <div className="pb-5 text-lg pl-3">{t("FREQUENCY")}</div>

                            <div className="flex justify-center items-center w-full pl-3">
                                Every <ReactiveFormInput
                                    label={t("FREQUENCY")}
                                    type="number"
                                    control={control}
                                    name="frequency"
                                    showLabel={false} />

                                <ReactiveFormSelect
                                    control={control}
                                    name="frequency_unit"
                                    label={t("FREQUENCY_UNIT")}
                                    options={[
                                        { value: 'month', label: t('MONTH') },
                                        { value: 'day', label: t('DAY') },
                                    ]}
                                    showLabel={false}
                                /> at

                                <ReactiveFormSelect
                                    control={control}
                                    name="frequency_time"
                                    label={t("FREQUENCY_TIME")}
                                    showLabel={false}
                                    options={[
                                        { value: '00:00', label: '00:00' },
                                        { value: '01:00', label: '01:00' },
                                        { value: '02:00', label: '02:00' },
                                        { value: '03:00', label: '03:00' },
                                        { value: '04:00', label: '04:00' },
                                        { value: '05:00', label: '05:00' },
                                        { value: '06:00', label: '06:00' },
                                        { value: '07:00', label: '07:00' },
                                        { value: '08:00', label: '08:00' },
                                        { value: '09:00', label: '09:00' },
                                        { value: '10:00', label: '10:00' },
                                        { value: '11:00', label: '11:00' },
                                        { value: '12:00', label: '12:00' },
                                        { value: '13:00', label: '13:00' },
                                        { value: '14:00', label: '14:00' },
                                        { value: '15:00', label: '15:00' },
                                        { value: '16:00', label: '16:00' },
                                        { value: '17:00', label: '17:00' },
                                        { value: '18:00', label: '18:00' },
                                        { value: '19:00', label: '19:00' },
                                        { value: '20:00', label: '20:00' },
                                        { value: '21:00', label: '21:00' },
                                        { value: '22:00', label: '22:00' },
                                        { value: '23:00', label: '23:00' },
                                    ]}
                                />
                            </div>
                        </div>
                    }

                    {
                        isASpecificDate &&
                        <div>
                            <div className="pb-5 text-lg pl-3">{t("SPECIFIC_DATE")}</div>

                            <div className="flex justify-center items-center w-full">
                                <ReactiveFormLocalDateInput
                                    control={control}
                                    type="date"
                                    className="md:w-1/3"
                                    label={t("DATE")}
                                    name={`specific_date`}
                                    isRequired
                                />
                                <ReactiveFormLocalDateInput
                                    control={control}
                                    type="time"
                                    className="md:w-1/3"
                                    label={t("TIME")}
                                    name={`specific_time`}
                                    isRequired
                                />
                                <ReactiveFormRadioButtonSelect
                                    className={"md:w-1/3"}
                                    name="is_specific_date_recurrent"
                                    label={t("IS_SPECIFIC_DATE_RECURRENT")}
                                    control={control}
                                    isRequired
                                    handleOnChange={handleOnChangeIsSpecificDate}
                                    options={[
                                        { label: t("NO"), value: "0" },
                                        { label: t("YES"), value: "1" }
                                    ]}
                                />
                            </div>
                        </div>
                    }

                    <ReactiveFormInput
                        label={t("DESCRIPTION")}
                        className="pt-2"
                        control={control}
                        name="description" />

                    <ReactiveFormInputArea
                        control={control}
                        className="pt-2"
                        label={t("MESSAGE")}
                        name="message"
                    />

                    <div className="flex flex-wrap px-3">
                        <button
                            type="button"
                            className="text-gold text-sm bg-neutral-100 rounded-full py-1 px-4 mr-3"
                            onClick={() => changeMessageValue("(@agent_name)")}>
                            {t("AGENT_NAME")}
                        </button>
                        <button
                            type="button"
                            className="text-gold text-sm bg-neutral-100 rounded-full py-1 px-4 mr-3"
                            onClick={() => changeMessageValue("(@agent_phone)")}>
                            {t("AGENT_PHONE")}
                        </button>
                        <button
                            type="button"
                            className="text-gold text-sm bg-neutral-100 rounded-full py-1 px-4 mr-3"
                            onClick={() => changeMessageValue("(@persons_name)")}>
                            {t("PERSONS_NAME")}
                        </button>
                        <button
                            type="button"
                            className="text-gold text-sm bg-neutral-100 rounded-full py-1 px-4 mr-3"
                            onClick={() => changeMessageValue("(@persons_phone)")}>
                            {t("PERSONS_PHONE")}
                        </button>
                    </div>

                </div>
            </Form>
        </Modal>
        <ToastContainer progressStyle={{ background: "#D4AF37" }} />
        { isLoadingFollowUpMessages && <LoadingMask />}
    </>)
}